/** Redstone Relay's first-run walkthrough on level 1: the steps and what each one accepts. */
import type { Board } from '@ka/fun';
import type { WalkthroughStep } from '@ka/ui';

/** Level 1: the power is at (0,1) and the ✓ lamp at (4,1); wire on (1..3, 1) lights it. */
export const WALK_LEVEL = 'first-spark';
const LINE = [1, 2, 3].map((x) => ({ x, y: 1 }));

export interface RedstoneWalkthroughHost {
  tile: () => Element | null | undefined;
  /** The square right of the power. */
  firstCell: () => Element | null | undefined;
  board: () => Element | null | undefined;
  run: () => Element | null | undefined;
  /** The result sheet, or the board while the run plays. */
  result: () => Element | null | undefined;
}

export function redstoneSteps(h: RedstoneWalkthroughHost): WalkthroughStep[] {
  return [
    { id: 'wire', say: 'Tap Wire', hint: 'Wire carries the power.', target: h.tile },
    {
      id: 'first',
      say: 'Tap the square next to the power',
      hint: 'The power is the red block.',
      target: h.firstCell,
    },
    {
      id: 'line',
      say: 'Keep going to the ✓ lamp',
      hint: 'Tap the next two squares.',
      target: h.board,
    },
    { id: 'run', say: 'Press Run', hint: 'Watch the power go.', target: h.run },
    {
      id: 'result',
      say: 'You lit the lamp!',
      hint: 'Now try the next puzzle.',
      target: h.result,
      button: 'Done',
    },
  ];
}

/** Whether a tap on square (x, y) belongs to step `id`. */
export function walkTapOk(id: string | null, x: number, y: number) {
  if (id === 'first') return x === 1 && y === 1;
  if (id === 'line') return y === 1 && (x === 2 || x === 3);
  return false;
}

/** Whether `board` finishes step `id`. */
export function walkBoardDone(id: string | null, board: Board) {
  const has = (c: { x: number; y: number }) =>
    board.placements.some((p) => p.x === c.x && p.y === c.y && p.piece === 'wire');
  if (id === 'first') return has(LINE[0]!);
  if (id === 'line') return LINE.every(has);
  return false;
}
